import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { DURATION } from './motion'

/**
 * `brass` is for REWARD ACTIONS ONLY — Claim, and nothing else.
 *
 * The deck reserves brass for reward, yield and caps, so a brass button that does not pay the
 * user out breaks the meaning of the colour everywhere else it appears. If a new action wants
 * to look important, it wants `primary`.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'brass'
type Size = 'sm' | 'md' | 'lg'

/**
 * Flat fills, not materials — in BOTH themes.
 *
 * This note used to end "and the light theme keeps its materials", which was the whole problem.
 * Light built these from gradient tokens, a top bevel, a bottom bevel and a coloured cast
 * shadow; dark used a flat fill and one 1px ring. The same `<Button variant="primary">` was a
 * machined enamel pill on one theme and a flat rectangle on the other, which is what makes two
 * themes read as two products. A cross-theme audit of every computed style on every route found
 * that these two variants accounted for 45 of the differences and that nothing else structural
 * differed at all — no radius, no padding, no font size.
 *
 * The materials were justified by a silver "machine hall" ground where a flat blue rectangle
 * had nothing to distinguish it from a link. That ground is gone — the page is flat white — and
 * a bevel needs a light source the page no longer has. The reference is flat in both its themes
 * for the same reason.
 *
 * This map stays theme-agnostic: every value still comes from a `--btn-*` token, so the two
 * themes differ in COLOUR and agree in shape, which is the whole point.
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    '[color:var(--btn-primary-fg)] [background:var(--btn-primary-bg)] [box-shadow:var(--btn-primary-shadow)] hover:brightness-110 hover:[box-shadow:var(--btn-primary-shadow-hi)]',
  secondary:
    'text-ink [background:var(--btn-ghost-bg)] [box-shadow:var(--btn-ghost-shadow)] hover:[background:var(--btn-ghost-bg-hi)]',
  ghost: 'bg-transparent text-muted hover:bg-accent-wash hover:text-ink',
  danger: 'bg-danger text-accent-ink hover:brightness-110',
  brass:
    '[color:var(--btn-brass-fg)] [background:var(--btn-brass-bg)] [box-shadow:var(--btn-brass-shadow)] [text-shadow:var(--btn-brass-textshadow)] hover:brightness-110',
}

/**
 * 12px rounds, matching the reference. The scale was full pills; a pill beside a 20px card
 * reads as a control from a different system, and the reference's own primary control is a
 * 12px round 32px tall.
 *
 * `sm` is 32px — the reference's Connect button exactly, down to its 14px w700 label. It was
 * `text-xs`, which put a 12px label on the app's most important control.
 */
const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3.5 text-sm font-bold rounded-[12px] gap-1.5',
  md: 'h-10 px-4.5 text-sm rounded-[12px] gap-2',
  lg: 'h-12 px-6 text-sm rounded-[12px] gap-2',
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
}

/**
 * The one button.
 *
 * `loading` disables interaction as well as showing the spinner — a user who can
 * double-submit a bond transaction because the button was merely *styled* as busy
 * has been failed by the component, not by themselves.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    iconLeft,
    iconRight,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading

  return (
    <motion.button
      ref={ref}
      whileHover={isDisabled ? undefined : { y: -1 }}
      whileTap={isDisabled ? undefined : { scale: 0.985 }}
      transition={{ duration: DURATION.instant }}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-semibold select-none',
        'transition-[box-shadow,background,color,filter] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : iconLeft}
      {children}
      {!loading && iconRight}
    </motion.button>
  )
})
